import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Container, Form, Modal } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaBalanceScale,
  FaBuilding,
  FaCheckCircle,
  FaChartLine,
  FaCog,
  FaEdit,
  FaExchangeAlt,
  FaExclamationTriangle,
  FaFileInvoiceDollar,
  FaPlus,
  FaMoneyCheckAlt,
  FaReceipt,
  FaSearch,
  FaTrash,
  FaWallet,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import AdminDataTable from './AdminDataTable';
import useAuth from '../../hooks/useAuth';
import { hasAccess, isAdminUser, isSuperAdminUser, readStoredAdminUser } from './adminAccessControl';
import {
  buildAccountingChecklist,
  summarizeAccountingChecklist,
  withCentralAccountStatus,
} from './accountingSetupConfig';
import { buildAccountingReportCenterPath } from './reportCenterLinks';
import './AccountingSetup.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const POS_API_BASE_URL = process.env.REACT_APP_API_PRODUCT_URL || '';

const isSuccessResponse = value => value === 0 || value === '0';
const ACCOUNT_TYPE_OPTIONS = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];
const EMPTY_CENTRAL_ACCOUNT_FORM = {
  id: '',
  account_id: '',
  account_name: '',
  account_type: 'Asset',
  balance: '0',
};
const FINANCE_CATEGORY_OPTIONS = [
  { value: 'rent', label: 'Sewa' },
  { value: 'utilities', label: 'Utilitas' },
  { value: 'maintenance', label: 'Perawatan' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'supplies', label: 'ATK / Supplies' },
  { value: 'salary_support', label: 'Support payroll' },
  { value: 'tax', label: 'Pajak / Retribusi' },
  { value: 'other', label: 'Lain-lain' },
];
const FINANCE_SETTLEMENT_OPTIONS = [
  { value: 'cash', label: 'Bayar tunai' },
  { value: 'bank_transfer', label: 'Transfer / Bank' },
  { value: 'qris', label: 'QRIS' },
  { value: 'other', label: 'Akun asset lain' },
  { value: 'payable', label: 'Catat sebagai hutang' },
];
const FINANCE_STATUS_LABELS = {
  paid: 'Paid',
  unpaid: 'Unpaid',
  partial_paid: 'Partial paid',
  cancelled: 'Cancelled',
};
const FINANCE_APPROVAL_LABELS = {
  not_required: 'Langsung posting',
  pending: 'Pending approval',
  approved: 'Approved',
  rejected: 'Rejected',
};
const OWNER_REPORT_FLOW = [
  { title: 'Daily control', copy: 'Cek beban keluar hari ini, hutang baru, dan dokumen yang jatuh tempo.' },
  { title: 'Weekly review', copy: 'Pantau kategori beban paling besar dan payment hutang yang sudah berjalan.' },
  { title: 'Monthly owner pack', copy: 'Laba rugi, neraca, hutang outstanding, dan expense breakdown outlet.' },
  { title: 'Audit trail', copy: 'Pastikan jurnal terbaru, akun lawan, dan nomor dokumen tercatat konsisten.' },
];

const getTodayInputValue = () => new Date().toISOString().split('T')[0];

const createEmptyFinanceForm = () => ({
  settlement_mode: 'cash',
  expense_date: getTodayInputValue(),
  due_date: getTodayInputValue(),
  vendor_name: '',
  category: 'utilities',
  description: '',
  reference_no: '',
  amount: '',
  expense_account_id: '',
  payment_account_id: '',
  payable_account_id: '',
  payment_method: 'cash',
  notes: '',
});

const createEmptyPayablePaymentForm = () => ({
  document_id: '',
  payment_date: getTodayInputValue(),
  amount: '',
  payment_method: 'bank_transfer',
  payment_account_id: '',
  notes: '',
});

const parseJsonResponse = async response => {
  const raw = await response.text();
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    return {
      error: 1,
      message: raw,
    };
  }
};

const formatCurrency = value =>
  `Rp ${Number(value || 0).toLocaleString('id-ID', {
    maximumFractionDigits: 0,
  })}`;

const isWholeRupiahValue = value => {
  const numericValue = Number(value || 0);
  return Number.isFinite(numericValue) && Math.abs(numericValue - Math.round(numericValue)) < 0.0001;
};

const normalizeApprovalStatus = value => String(value || '').trim().toLowerCase() || 'not_required';

const isFinanceDocumentApprovedForLedger = value =>
  ['not_required', 'approved'].includes(normalizeApprovalStatus(value));

const formatShortDate = value => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};
const formatDateTime = value => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
const formatMonthYear = (year, month) => {
  if (!year || !month) {
    return '-';
  }

  return new Date(year, month - 1, 1).toLocaleDateString('id-ID', {
    month: 'long',
    year: 'numeric',
  });
};
const humanizeToken = value =>
  String(value || '')
    .split('_')
    .filter(Boolean)
    .map(chunk => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ') || '-';

const formatOutletRecord = outlet => ({
  id: String(outlet?.id ?? outlet?.ID ?? ''),
  name: outlet?.outlet_name || outlet?.OutletName || outlet?.name || `Outlet #${outlet?.id || outlet?.ID || '-'}`,
});

const formatCentralAccountRecord = account => ({
  id: String(account?.id ?? account?.ID ?? ''),
  account_id: String(account?.account_id ?? account?.AccountID ?? '').trim(),
  account_name: account?.account_name ?? account?.AccountName ?? '',
  account_type: account?.account_type ?? account?.AccountType ?? '',
  balance:
    account?.balance?.Float64 ??
    account?.Balance?.Float64 ??
    account?.balance ??
    account?.Balance ??
    0,
});

const getStatusVariant = value => {
  if (value) {
    return '';
  }
  return 'is-danger';
};

const getFinanceStatusVariant = value => {
  switch (String(value || '').toLowerCase()) {
    case 'paid':
      return '';
    case 'partial_paid':
      return 'is-warning';
    case 'cancelled':
      return 'is-danger';
    default:
      return 'is-danger';
  }
};

const getFinanceApprovalVariant = value => {
  switch (normalizeApprovalStatus(value)) {
    case 'pending':
      return 'is-warning';
    case 'rejected':
      return 'is-danger';
    default:
      return '';
  }
};

function AccountingSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAndRefreshToken } = useAuth();
  const currentUser = readStoredAdminUser();
  const canAccess = isAdminUser(currentUser);
  const canManageCentralAccounts = isSuperAdminUser(currentUser);
  const canOpenAnalyticsCenter = hasAccess('/report-center', currentUser);
  const queryOutletId = useMemo(() => new URLSearchParams(location.search).get('outlet_id') || '', [location.search]);
  const currentPeriod = useMemo(() => {
    const today = new Date();
    return {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      label: formatMonthYear(today.getFullYear(), today.getMonth() + 1),
      inputDate: getTodayInputValue(),
    };
  }, []);
  const [authToken, setAuthToken] = useState('');
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(queryOutletId);
  const [accounts, setAccounts] = useState([]);
  const [centralAccounts, setCentralAccounts] = useState([]);
  const [feeSetting, setFeeSetting] = useState(null);
  const [feeInput, setFeeInput] = useState('');
  const [syncSummary, setSyncSummary] = useState(null);
  const [profitLoss, setProfitLoss] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingFee, setSavingFee] = useState(false);
  const [showCentralAccountModal, setShowCentralAccountModal] = useState(false);
  const [savingCentralAccount, setSavingCentralAccount] = useState(false);
  const [centralAccountForm, setCentralAccountForm] = useState(EMPTY_CENTRAL_ACCOUNT_FORM);
  const [financeForm, setFinanceForm] = useState(createEmptyFinanceForm);
  const [payablePaymentForm, setPayablePaymentForm] = useState(createEmptyPayablePaymentForm);
  const [financeDocuments, setFinanceDocuments] = useState([]);
  const [financePayments, setFinancePayments] = useState([]);
  const [ownerSummary, setOwnerSummary] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [savingFinanceDocument, setSavingFinanceDocument] = useState(false);
  const [savingFinancePayment, setSavingFinancePayment] = useState(false);
  const feeSectionRef = useRef(null);
  const showEmbeddedFitnessFinance = false;

  const resolveToken = useCallback(async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return '';
    }

    const isValid = await verifyAndRefreshToken(token);
    if (!isValid) {
      navigate('/loginadmin');
      return '';
    }

    const refreshedToken = localStorage.getItem('sessionToken');
    if (!refreshedToken) {
      navigate('/loginadmin');
      return '';
    }

    return refreshedToken;
  }, [navigate, verifyAndRefreshToken]);

  const fetchOutlets = useCallback(async token => {
    const response = await fetch(`${API_BASE_URL}outletlist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ limit: 200, start: 0 }),
    });
    const data = await response.json();
    if (!response.ok || !isSuccessResponse(data.error)) {
      throw new Error(data.message || 'Failed to fetch outlets');
    }

    const normalizedOutlets = (Array.isArray(data.results) ? data.results : []).map(formatOutletRecord);
    setOutlets(normalizedOutlets);
    setSelectedOutlet(prev => {
      if (prev) {
        return prev;
      }
      if (queryOutletId) {
        return queryOutletId;
      }
      return normalizedOutlets[0]?.id || '';
    });
  }, [queryOutletId]);

  const fetchCentralAccounts = useCallback(async token => {
    const response = await fetch(`${API_BASE_URL}accounting/accounts?limit=500`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await parseJsonResponse(response);
    if (!response.ok || !isSuccessResponse(data.error)) {
      throw new Error(data.message || 'Failed to fetch central accounts');
    }
    setCentralAccounts((Array.isArray(data.results) ? data.results : []).map(formatCentralAccountRecord));
  }, []);

  const fetchAccountingReports = useCallback(async (token, outletId) => {
    const today = new Date();
    const payload = {
      outlet_id: Number(outletId),
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      source_system: 'pos',
      business_unit: 'pos',
    };

    const [profitResponse, balanceResponse] = await Promise.all([
      fetch(`${API_BASE_URL}profit-loss`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      }),
      fetch(`${API_BASE_URL}balance-sheet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      }),
    ]);

    const [profitData, balanceData] = await Promise.all([
      parseJsonResponse(profitResponse),
      parseJsonResponse(balanceResponse),
    ]);

    if (profitResponse.ok && isSuccessResponse(profitData.error)) {
      setProfitLoss(profitData.data || null);
    } else {
      setProfitLoss(null);
    }

    if (balanceResponse.ok && isSuccessResponse(balanceData.error)) {
      setBalanceSheet(balanceData.data || null);
    } else {
      setBalanceSheet(null);
    }
  }, []);

  const fetchFitnessFinanceData = useCallback(async (token, outletId) => {
    if (!outletId) {
      return;
    }

    setFinanceLoading(true);
    try {
      const [documentsResponse, paymentsResponse, summaryResponse] = await Promise.all([
        fetch(`${API_BASE_URL}fitness-finance/document/list`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            outlet_id: Number(outletId),
            limit: 20,
            offset: 0,
          }),
        }),
        fetch(`${API_BASE_URL}fitness-finance/payment/list`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            outlet_id: Number(outletId),
            limit: 20,
            offset: 0,
          }),
        }),
        fetch(`${API_BASE_URL}fitness-finance/report/owner-summary`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            outlet_id: Number(outletId),
            year: currentPeriod.year,
            month: currentPeriod.month,
            recent_limit: 8,
          }),
        }),
      ]);

      const [documentsData, paymentsData, summaryData] = await Promise.all([
        parseJsonResponse(documentsResponse),
        parseJsonResponse(paymentsResponse),
        parseJsonResponse(summaryResponse),
      ]);

      if (!documentsResponse.ok || !isSuccessResponse(documentsData?.error)) {
        throw new Error(documentsData?.message || 'Failed to load finance documents');
      }
      if (!paymentsResponse.ok || !isSuccessResponse(paymentsData?.error)) {
        throw new Error(paymentsData?.message || 'Failed to load finance payments');
      }
      if (!summaryResponse.ok || !isSuccessResponse(summaryData?.error)) {
        throw new Error(summaryData?.message || 'Failed to load owner summary');
      }

      setFinanceDocuments(Array.isArray(documentsData?.results) ? documentsData.results : []);
      setFinancePayments(Array.isArray(paymentsData?.results) ? paymentsData.results : []);
      setOwnerSummary(summaryData?.data?.summary || null);
    } catch (fetchError) {
      console.error('Failed to fetch fitness finance data:', fetchError);
      setFinanceDocuments([]);
      setFinancePayments([]);
      setOwnerSummary(null);
      Swal.fire('Error', fetchError.message || 'Failed to fetch fitness finance data', 'error');
    } finally {
      setFinanceLoading(false);
    }
  }, [currentPeriod.month, currentPeriod.year]);

  const fetchSetupData = useCallback(async (token, outletId) => {
    if (!outletId) {
      return;
    }

    setLoading(true);
    try {
      const [accountsResponse, feeResponse, syncResponse] = await Promise.all([
        fetch(`${POS_API_BASE_URL}api/accounts/?outlet_id=${encodeURIComponent(outletId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${POS_API_BASE_URL}api/outletfee/?outlet_id=${encodeURIComponent(outletId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${POS_API_BASE_URL}api/accounting/sync/summary?outlet_id=${encodeURIComponent(outletId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const [accountsData, feeData, syncData] = await Promise.all([
        accountsResponse.json(),
        feeResponse.ok ? feeResponse.json() : Promise.resolve(null),
        syncResponse.json(),
      ]);

      if (!accountsResponse.ok) {
        throw new Error(accountsData?.error || 'Failed to fetch POS accounts');
      }
      if (!syncResponse.ok || !isSuccessResponse(syncData.error)) {
        throw new Error(syncData?.message || syncData?.error || 'Failed to fetch sync summary');
      }

      setAccounts(Array.isArray(accountsData) ? accountsData : []);
      setSyncSummary(syncData.data || null);

      if (feeResponse.ok && feeData && typeof feeData === 'object' && feeData.outlet_id) {
        setFeeSetting(feeData);
        setFeeInput(String(feeData.fee_setting ?? feeData.FeeSetting ?? ''));
      } else {
        setFeeSetting(null);
        setFeeInput('');
      }

      await fetchAccountingReports(token, outletId);
    } catch (fetchError) {
      console.error('Failed to fetch accounting setup data:', fetchError);
      setAccounts([]);
      setFeeSetting(null);
      setSyncSummary(null);
      setProfitLoss(null);
      setBalanceSheet(null);
      Swal.fire('Error', fetchError.message || 'Failed to fetch accounting setup', 'error');
    } finally {
      setLoading(false);
    }
  }, [fetchAccountingReports]);

  useEffect(() => {
    if (!canAccess) {
      navigate('/dashboardadmin', { replace: true });
      return;
    }

    let active = true;
    const initialize = async () => {
      const token = await resolveToken();
      if (!active || !token) {
        return;
      }

      setAuthToken(token);
      try {
        await Promise.all([fetchOutlets(token), fetchCentralAccounts(token)]);
      } catch (error) {
        console.error('Failed to initialize accounting setup:', error);
        Swal.fire('Error', error.message || 'Failed to initialize accounting setup', 'error');
      }
    };

    initialize();

    return () => {
      active = false;
    };
  }, [canAccess, fetchCentralAccounts, fetchOutlets, navigate, resolveToken]);

  useEffect(() => {
    if (!authToken || !selectedOutlet) {
      return;
    }

    fetchSetupData(authToken, selectedOutlet);
  }, [authToken, fetchSetupData, selectedOutlet]);

  useEffect(() => {
    if (!showEmbeddedFitnessFinance || !authToken || !selectedOutlet) {
      return;
    }

    fetchFitnessFinanceData(authToken, selectedOutlet);
  }, [authToken, fetchFitnessFinanceData, selectedOutlet, showEmbeddedFitnessFinance]);

  const checklist = useMemo(() => {
    const rawChecklist = buildAccountingChecklist(accounts);
    return withCentralAccountStatus(rawChecklist, centralAccounts);
  }, [accounts, centralAccounts]);

  const checklistSummary = useMemo(() => summarizeAccountingChecklist(checklist), [checklist]);
  const missingCoreItems = useMemo(
    () => checklist.filter(item => item.importance === 'core' && !item.configured && !item.conditional),
    [checklist]
  );
  const selectedOutletData = useMemo(
    () => outlets.find(outlet => String(outlet.id) === String(selectedOutlet)) || null,
    [outlets, selectedOutlet]
  );
  const readinessPercent = checklistSummary.coreTotal
    ? Math.round((checklistSummary.coreReady / checklistSummary.coreTotal) * 100)
    : 0;
  const expenseAccounts = useMemo(
    () => centralAccounts.filter(account => String(account.account_type).toLowerCase() === 'expense'),
    [centralAccounts]
  );
  const assetAccounts = useMemo(
    () => centralAccounts.filter(account => String(account.account_type).toLowerCase() === 'asset'),
    [centralAccounts]
  );
  const liabilityAccounts = useMemo(
    () => centralAccounts.filter(account => String(account.account_type).toLowerCase() === 'liability'),
    [centralAccounts]
  );
  const outstandingPayableDocuments = useMemo(
    () =>
      financeDocuments.filter(
        item => Number(item?.outstanding_amount || 0) > 0 && isFinanceDocumentApprovedForLedger(item?.approval_status)
      ),
    [financeDocuments]
  );

  useEffect(() => {
    if (expenseAccounts.length > 0) {
      setFinanceForm(prev => (prev.expense_account_id ? prev : { ...prev, expense_account_id: expenseAccounts[0].account_id }));
    }
    if (assetAccounts.length > 0) {
      setFinanceForm(prev => (prev.payment_account_id ? prev : { ...prev, payment_account_id: assetAccounts[0].account_id }));
      setPayablePaymentForm(prev => (prev.payment_account_id ? prev : { ...prev, payment_account_id: assetAccounts[0].account_id }));
    }
    if (liabilityAccounts.length > 0) {
      setFinanceForm(prev => (prev.payable_account_id ? prev : { ...prev, payable_account_id: liabilityAccounts[0].account_id }));
    }
  }, [assetAccounts, expenseAccounts, liabilityAccounts]);

  useEffect(() => {
    if (!payablePaymentForm.document_id) {
      return;
    }

    const selectedDocument = outstandingPayableDocuments.find(item => String(item.id) === String(payablePaymentForm.document_id));
    if (!selectedDocument) {
      return;
    }

    setPayablePaymentForm(prev => (
      prev.amount
        ? prev
        : { ...prev, amount: String(Number(selectedDocument.outstanding_amount || 0)) }
    ));
  }, [outstandingPayableDocuments, payablePaymentForm.document_id]);

  const handleSaveFee = async event => {
    event.preventDefault();
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }

    setSavingFee(true);
    try {
      const response = await fetch(`${POS_API_BASE_URL}api/outletfee/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          outlet_id: Number(selectedOutlet),
          fee_setting: Number(feeInput || 0),
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save fee setting');
      }

      await fetchSetupData(authToken, selectedOutlet);
      Swal.fire('Success', 'Referensi billing outlet berhasil disimpan.', 'success');
    } catch (error) {
      console.error('Failed to save outlet fee:', error);
      Swal.fire('Error', error.message || 'Gagal menyimpan referensi billing outlet', 'error');
    } finally {
      setSavingFee(false);
    }
  };

  const resetFinanceForm = useCallback(() => {
    setFinanceForm(prev => ({
      ...createEmptyFinanceForm(),
      expense_account_id: prev.expense_account_id || expenseAccounts[0]?.account_id || '',
      payment_account_id: prev.payment_account_id || assetAccounts[0]?.account_id || '',
      payable_account_id: prev.payable_account_id || liabilityAccounts[0]?.account_id || '',
    }));
  }, [assetAccounts, expenseAccounts, liabilityAccounts]);

  const resetPayablePaymentForm = useCallback(() => {
    setPayablePaymentForm(prev => ({
      ...createEmptyPayablePaymentForm(),
      payment_account_id: prev.payment_account_id || assetAccounts[0]?.account_id || '',
    }));
  }, [assetAccounts]);

  const handleSaveFinanceDocument = useCallback(async event => {
    event.preventDefault();
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }

    if (!financeForm.description.trim()) {
      Swal.fire('Error', 'Deskripsi beban wajib diisi.', 'error');
      return;
    }
    if (Number(financeForm.amount || 0) <= 0) {
      Swal.fire('Error', 'Nominal finance wajib lebih dari 0.', 'error');
      return;
    }
    if (!isWholeRupiahValue(financeForm.amount)) {
      Swal.fire('Error', 'Nominal finance harus rupiah bulat tanpa sen.', 'error');
      return;
    }

    setSavingFinanceDocument(true);
    try {
      const response = await fetch(`${API_BASE_URL}fitness-finance/document/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          outlet_id: Number(selectedOutlet),
          document_type: financeForm.settlement_mode === 'payable' ? 'payable_bill' : 'expense',
          settlement_mode: financeForm.settlement_mode,
          expense_date: financeForm.expense_date,
          due_date: financeForm.settlement_mode === 'payable' ? financeForm.due_date : '',
          vendor_name: financeForm.vendor_name,
          category: financeForm.category,
          description: financeForm.description,
          reference_no: financeForm.reference_no,
          amount: Number(financeForm.amount || 0),
          expense_account_id: financeForm.expense_account_id,
          payment_account_id: financeForm.settlement_mode === 'payable' ? '' : financeForm.payment_account_id,
          payable_account_id: financeForm.settlement_mode === 'payable' ? financeForm.payable_account_id : '',
          payment_method: financeForm.settlement_mode === 'payable' ? '' : financeForm.payment_method,
          notes: financeForm.notes,
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok || !isSuccessResponse(data?.error)) {
        throw new Error(data?.message || 'Failed to save finance document');
      }

      await fetchFitnessFinanceData(authToken, selectedOutlet);
      resetFinanceForm();
      Swal.fire('Success', data?.message || 'Dokumen finance berhasil disimpan.', 'success');
    } catch (error) {
      console.error('Failed to save finance document:', error);
      Swal.fire('Error', error.message || 'Failed to save finance document', 'error');
    } finally {
      setSavingFinanceDocument(false);
    }
  }, [authToken, fetchFitnessFinanceData, financeForm, resetFinanceForm, selectedOutlet]);

  const handleSavePayablePayment = useCallback(async event => {
    event.preventDefault();
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }

    if (!payablePaymentForm.document_id) {
      Swal.fire('Error', 'Pilih dokumen hutang yang akan dibayar.', 'error');
      return;
    }
    if (Number(payablePaymentForm.amount || 0) <= 0) {
      Swal.fire('Error', 'Nominal payment wajib lebih dari 0.', 'error');
      return;
    }

    const activePayableDocument = outstandingPayableDocuments.find(
      item => String(item.id) === String(payablePaymentForm.document_id)
    );
    const payableAmount = Number(payablePaymentForm.amount || 0);
    const outstandingAmount = Number(activePayableDocument?.outstanding_amount || 0);
    const allowsLegacyFraction =
      activePayableDocument &&
      !isWholeRupiahValue(outstandingAmount) &&
      Math.abs(payableAmount - outstandingAmount) < 0.0001;
    if (!allowsLegacyFraction && !isWholeRupiahValue(payablePaymentForm.amount)) {
      Swal.fire('Error', 'Nominal payment harus rupiah bulat tanpa sen.', 'error');
      return;
    }

    setSavingFinancePayment(true);
    try {
      const response = await fetch(`${API_BASE_URL}fitness-finance/payment/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          outlet_id: Number(selectedOutlet),
          document_id: Number(payablePaymentForm.document_id),
          payment_date: payablePaymentForm.payment_date,
          amount: Number(payablePaymentForm.amount || 0),
          payment_method: payablePaymentForm.payment_method,
          payment_account_id: payablePaymentForm.payment_account_id,
          notes: payablePaymentForm.notes,
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok || !isSuccessResponse(data?.error)) {
        throw new Error(data?.message || 'Failed to save payable payment');
      }

      await fetchFitnessFinanceData(authToken, selectedOutlet);
      resetPayablePaymentForm();
      Swal.fire('Success', data?.message || 'Pembayaran hutang berhasil disimpan.', 'success');
    } catch (error) {
      console.error('Failed to save payable payment:', error);
      Swal.fire('Error', error.message || 'Failed to save payable payment', 'error');
    } finally {
      setSavingFinancePayment(false);
    }
  }, [authToken, fetchFitnessFinanceData, outstandingPayableDocuments, payablePaymentForm, resetPayablePaymentForm, selectedOutlet]);

  const openCentralAccountModal = useCallback(account => {
    if (account) {
      setCentralAccountForm({
        id: String(account.id || ''),
        account_id: account.account_id || '',
        account_name: account.account_name || '',
        account_type: account.account_type || 'Asset',
        balance: String(account.balance ?? 0),
      });
    } else {
      setCentralAccountForm(EMPTY_CENTRAL_ACCOUNT_FORM);
    }
    setShowCentralAccountModal(true);
  }, []);

  const closeCentralAccountModal = useCallback(() => {
    setShowCentralAccountModal(false);
    setCentralAccountForm(EMPTY_CENTRAL_ACCOUNT_FORM);
  }, []);

  const handleSaveCentralAccount = useCallback(async event => {
    event.preventDefault();
    if (!canManageCentralAccounts) {
      Swal.fire('Forbidden', 'Hanya superadmin yang bisa mengubah akun pusat.', 'error');
      return;
    }

    setSavingCentralAccount(true);
    try {
      const method = centralAccountForm.id ? 'PUT' : 'POST';
      const response = await fetch(`${API_BASE_URL}accounting/accounts`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          id: Number(centralAccountForm.id || 0),
          account_id: centralAccountForm.account_id,
          account_name: centralAccountForm.account_name,
          account_type: centralAccountForm.account_type,
          balance: Number(centralAccountForm.balance || 0),
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok || !isSuccessResponse(data?.error)) {
        throw new Error(data?.message || 'Failed to save central account');
      }

      await fetchCentralAccounts(authToken);
      closeCentralAccountModal();
      Swal.fire('Success', data.message || 'Akun pusat berhasil disimpan.', 'success');
    } catch (error) {
      console.error('Failed to save central account:', error);
      Swal.fire('Error', error.message || 'Failed to save central account', 'error');
    } finally {
      setSavingCentralAccount(false);
    }
  }, [authToken, canManageCentralAccounts, centralAccountForm, closeCentralAccountModal, fetchCentralAccounts]);

  const handleDeleteCentralAccount = useCallback(async account => {
    if (!canManageCentralAccounts) {
      Swal.fire('Forbidden', 'Hanya superadmin yang bisa menghapus akun pusat.', 'error');
      return;
    }

    const confirmation = await Swal.fire({
      title: 'Hapus akun pusat?',
      text: `${account.account_id} - ${account.account_name}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}accounting/accounts`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ id: Number(account.id) }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok || !isSuccessResponse(data?.error)) {
        throw new Error(data?.message || 'Failed to delete central account');
      }

      await fetchCentralAccounts(authToken);
      Swal.fire('Deleted', data.message || 'Akun pusat berhasil dihapus.', 'success');
    } catch (error) {
      console.error('Failed to delete central account:', error);
      Swal.fire('Error', error.message || 'Failed to delete central account', 'error');
    }
  }, [authToken, canManageCentralAccounts, fetchCentralAccounts]);

  const openChecklistAction = useCallback(row => {
    if (!selectedOutlet) {
      Swal.fire('Info', 'Pilih outlet lebih dulu.', 'info');
      return;
    }

    if (row.isFeeSetting) {
      feeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const params = new URLSearchParams({ outlet_id: String(selectedOutlet) });
    if (row.key) {
      params.set('template', row.key);
    }
    if (row.account?.id) {
      params.set('account_id', String(row.account.id));
    }
    navigate(`/pos-account-mappings?${params.toString()}`);
  }, [navigate, selectedOutlet]);

  const columns = [
    {
      name: 'Checklist',
      sortable: true,
      grow: 1.5,
      cell: row => (
        <div className="table-identity">
          <span className="table-identity__avatar">
            {row.label?.slice(0, 2).toUpperCase() || 'AC'}
          </span>
          <span className="table-identity__meta">
            <strong>{row.label}</strong>
            <small>{row.description}</small>
          </span>
        </div>
      ),
    },
    {
      name: 'Mapping',
      grow: 1.1,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.isFeeSetting ? 'outlet fee' : row.key}</strong>
          <small>{row.importance === 'core' ? 'Core requirement' : 'Extended / conditional'}</small>
        </span>
      ),
    },
    {
      name: 'POS Account',
      grow: 1.4,
      cell: row => (
        row.account ? (
          <span className="table-identity__meta">
            <strong>{row.account.id}</strong>
            <small>{row.account.name}</small>
          </span>
        ) : (
          <span className="status-pill is-danger">Missing</span>
        )
      ),
    },
    {
      name: 'Ledger Match',
      width: '180px',
      cell: row => (
        <span className={`status-pill ${row.centralMatched ? '' : 'is-danger'}`}>
          {row.account ? (row.centralMatched ? 'Matched' : 'Missing in ledger') : (row.isFeeSetting ? 'Configured separately' : 'No account')}
        </span>
      ),
    },
    {
      name: 'Status',
      width: '170px',
      cell: row => (
        <span className={`status-pill ${getStatusVariant(row.configured)}`}>
          {row.configured ? 'Ready' : row.conditional ? 'Optional' : 'Needs setup'}
        </span>
      ),
    },
    {
      name: 'Action',
      width: '170px',
      cell: row => (
        <div className="table-action-group">
          <Button
            variant={row.configured ? 'outline-warning' : 'primary'}
            size="sm"
            onClick={() => openChecklistAction(row)}
          >
            {row.configured ? <FaEdit /> : <FaMoneyCheckAlt />} {row.configured ? 'Edit' : 'Setup'}
          </Button>
        </div>
      ),
    },
  ];

  const centralAccountColumns = [
    {
      name: 'Account ID',
      sortable: true,
      width: '140px',
      cell: row => <strong>{row.account_id}</strong>,
    },
    {
      name: 'Account name',
      sortable: true,
      grow: 1.6,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.account_name}</strong>
          <small>Ledger pusat global</small>
        </span>
      ),
    },
    {
      name: 'Type',
      sortable: true,
      width: '140px',
      cell: row => <span className="status-pill">{row.account_type}</span>,
    },
    {
      name: 'Balance',
      sortable: true,
      width: '170px',
      cell: row => <span>{formatCurrency(row.balance)}</span>,
    },
    {
      name: 'Action',
      width: '190px',
      cell: row => (
        canManageCentralAccounts ? (
          <div className="table-action-group">
            <Button variant="outline-secondary" size="sm" onClick={() => openCentralAccountModal(row)}>
              <FaEdit /> Edit
            </Button>
            <Button variant="outline-danger" size="sm" onClick={() => handleDeleteCentralAccount(row)}>
              <FaTrash /> Delete
            </Button>
          </div>
        ) : (
          <span className="status-pill">View only</span>
        )
      ),
    },
  ];
  const selectedPayableDocument = useMemo(
    () => outstandingPayableDocuments.find(item => String(item.id) === String(payablePaymentForm.document_id)) || null,
    [outstandingPayableDocuments, payablePaymentForm.document_id]
  );

  const financeDocumentColumns = [
    {
      name: 'Dokumen',
      sortable: true,
      grow: 1.6,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.document_no}</strong>
          <small>{row.description}</small>
        </span>
      ),
    },
    {
      name: 'Kategori',
      width: '180px',
      cell: row => (
        <span className="table-identity__meta">
          <strong>{humanizeToken(row.category)}</strong>
          <small>{row.vendor_name || (row.settlement_mode === 'payable' ? 'Hutang usaha' : 'Beban langsung')}</small>
        </span>
      ),
    },
    {
      name: 'Tanggal',
      width: '180px',
      cell: row => (
        <span className="table-identity__meta">
          <strong>{formatShortDate(row.expense_date)}</strong>
          <small>{row.due_date ? `Jatuh tempo ${formatShortDate(row.due_date)}` : 'Tidak ada due date'}</small>
        </span>
      ),
    },
    {
      name: 'Nominal',
      width: '210px',
      cell: row => (
        <span className="table-identity__meta">
          <strong>{formatCurrency(row.amount)}</strong>
          <small>Terbayar {formatCurrency(row.paid_amount)} | Sisa {formatCurrency(row.outstanding_amount)}</small>
        </span>
      ),
    },
    {
      name: 'Status',
      width: '160px',
      cell: row => (
        <span className={`status-pill ${getFinanceStatusVariant(row.status)}`}>
          {FINANCE_STATUS_LABELS[row.status] || humanizeToken(row.status)}
        </span>
      ),
    },
    {
      name: 'Approval',
      width: '230px',
      cell: row => {
        const approvalStatus = normalizeApprovalStatus(row.approval_status);
        const detailMessage =
          approvalStatus === 'pending'
            ? `Diajukan ${formatDateTime(row.approval_requested_at)}`
            : approvalStatus === 'approved'
              ? `Disetujui ${row.approval_decided_by_name || row.approval_decided_by_actor || 'manager/admin'}`
              : approvalStatus === 'rejected'
                ? row.approval_note || 'Dokumen ditolak'
                : 'Tidak butuh approval tambahan';

        return (
          <span className="table-identity__meta">
            <span className={`status-pill ${getFinanceApprovalVariant(approvalStatus)}`}>
              {FINANCE_APPROVAL_LABELS[approvalStatus] || humanizeToken(approvalStatus)}
            </span>
            <small>{detailMessage}</small>
            <small>{isFinanceDocumentApprovedForLedger(approvalStatus) ? 'Sudah boleh memengaruhi jurnal' : 'Belum masuk jurnal pusat'}</small>
          </span>
        );
      },
    },
  ];

  const financePaymentColumns = [
    {
      name: 'Payment',
      sortable: true,
      grow: 1.4,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.payment_no}</strong>
          <small>{row.document_no || row.document_description || 'Dokumen hutang'}</small>
        </span>
      ),
    },
    {
      name: 'Tanggal',
      width: '170px',
      cell: row => (
        <span className="table-identity__meta">
          <strong>{formatShortDate(row.payment_date)}</strong>
          <small>{humanizeToken(row.payment_method)}</small>
        </span>
      ),
    },
    {
      name: 'Nominal',
      width: '180px',
      cell: row => <strong>{formatCurrency(row.amount)}</strong>,
    },
    {
      name: 'Dokumen',
      grow: 1.4,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.document_description || '-'}</strong>
          <small>{row.vendor_name || row.document_status || '-'}</small>
        </span>
      ),
    },
  ];

  const recentJournalColumns = [
    {
      name: 'Waktu',
      width: '180px',
      cell: row => (
        <span className="table-identity__meta">
          <strong>{formatShortDate(row.occurred_at)}</strong>
          <small>{humanizeToken(row.source_type)}</small>
        </span>
      ),
    },
    {
      name: 'Deskripsi',
      grow: 1.8,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.description}</strong>
          <small>{row.debit_account} - {row.credit_account}</small>
        </span>
      ),
    },
    {
      name: 'Nilai',
      width: '170px',
      cell: row => <strong>{formatCurrency(row.amount)}</strong>,
    },
  ];

  return (
    <div>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaCog}
          eyebrow="Accounting control"
          title="Accounting Control"
          description="Siapkan mapping akun POS dan validasi account ID ke ledger pusat agar posting jurnal lintas sistem tetap rapi. Billing outlet bulanan dikelola terpisah dari sale POS."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Accounting Control' },
          ]}
          meta={(
            <div className="d-flex flex-wrap gap-2">
              <span className="soft-stat">
                <FaCheckCircle /> {checklistSummary.coreReady}/{checklistSummary.coreTotal || 0} core ready
              </span>
              <span className="soft-stat">
                <FaBalanceScale /> {selectedOutletData?.name || 'Select outlet'}
              </span>
            </div>
          )}
          actions={(
            <div className="d-flex flex-wrap gap-2">
              {canManageCentralAccounts ? (
                <Button
                  variant="dark"
                  onClick={() => openCentralAccountModal(null)}
                >
                  <FaPlus /> Tambah akun pusat
                </Button>
              ) : null}
              <Button
                variant="primary"
                onClick={() => navigate(buildAccountingReportCenterPath('trial-balance', { outletId: selectedOutlet }))}
                disabled={!canOpenAnalyticsCenter}
              >
                <FaChartLine /> Analytics Center
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(`/pos-account-mappings?outlet_id=${selectedOutlet}`)}
                disabled={!selectedOutlet}
              >
                <FaWallet /> Add/Edit Account Mapping
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(`/operasional-finance?outlet_id=${selectedOutlet}`)}
                disabled={!selectedOutlet}
              >
                <FaReceipt /> Expenses & Payables
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(`/asset-management?outlet_id=${selectedOutlet}`)}
                disabled={!selectedOutlet}
              >
                <FaBuilding /> Assets & Investments
              </Button>
              <Button
                variant="primary"
                onClick={() => navigate(`/pos-sync-monitor?outlet_id=${selectedOutlet}`)}
                disabled={!selectedOutlet}
              >
                <FaExchangeAlt /> POS Sync Monitor
              </Button>
            </div>
          )}
        />

        <div className="section-card accounting-filter-card">
          <div className="table-toolbar">
            <div className="control-with-icon search-control accounting-outlet-picker">
              <span className="control-with-icon__icon">
                <FaSearch />
              </span>
              <Form.Select value={selectedOutlet} onChange={event => setSelectedOutlet(event.target.value)}>
                <option value="">Select outlet</option>
                {outlets.map(outlet => (
                  <option key={outlet.id} value={outlet.id}>
                    {outlet.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="accounting-filter-card__note">
              Semua account ID yang dipakai di POS dan Expenses & Payables harus identik dengan `tbaccounts.account_id` di ledger pusat.
            </div>
          </div>
        </div>

        {missingCoreItems.length > 0 ? (
          <div className="section-card accounting-alert-card">
            <div className="accounting-alert-card__copy">
              <span className="page-eyebrow">Action required</span>
              <h2>{missingCoreItems.length} core item masih butuh setup</h2>
              <p>
                Checklist di bawah sekarang punya tombol `Setup` langsung. Untuk akun POS, tombol itu akan membuka form mapping.
                Untuk fee setting, tombolnya akan langsung lompat ke form fee.
              </p>
            </div>
            <div className="accounting-alert-card__actions">
              <Button
                variant="primary"
                onClick={() => openChecklistAction(missingCoreItems[0])}
                disabled={missingCoreItems.length === 0}
              >
                <FaMoneyCheckAlt /> Setup first missing item
              </Button>
            </div>
          </div>
        ) : null}

        <div className="accounting-kpi-grid">
          <section className="section-card accounting-kpi-card">
            <span className="page-eyebrow">Core readiness</span>
            <h3>{readinessPercent}%</h3>
            <p>{checklistSummary.coreReady} dari {checklistSummary.coreTotal || 0} requirement inti sudah siap.</p>
          </section>
          <section className="section-card accounting-kpi-card">
            <span className="page-eyebrow">Sync backlog</span>
            <h3>{syncSummary?.needs_retry || 0}</h3>
            <p>{syncSummary?.failed || 0} failed, {syncSummary?.pending || 0} pending.</p>
          </section>
          <section className="section-card accounting-kpi-card">
            <span className="page-eyebrow">Billing outlet</span>
            <h3>{feeSetting ? 'Tersimpan' : 'Opsional'}</h3>
            <p>Referensi billing bulanan tidak dipakai sebagai potongan transaksi sale POS.</p>
          </section>
          <section className="section-card accounting-kpi-card">
            <span className="page-eyebrow">Ledger validation</span>
            <h3>{checklistSummary.centralMatched}/{checklistSummary.total || 0}</h3>
            <p>Item checklist yang account ID-nya sudah match ke chart of accounts pusat.</p>
          </section>
        </div>

        <div className="accounting-panel-grid">
          <section className="section-card accounting-insight-card" ref={feeSectionRef}>
            <div className="accounting-section-head">
              <div>
                <span className="page-eyebrow">Billing outlet</span>
                <h2>Referensi biaya langganan bulanan</h2>
                <p>Nilai di bawah hanya sebagai catatan billing outlet. Sale POS tidak memotong fee transaksi dan sync jurnal tidak membutuhkannya.</p>
              </div>
              <span className={`status-pill ${feeSetting ? '' : 'is-danger'}`}>
                {feeSetting ? 'Tersimpan' : 'Opsional'}
              </span>
            </div>

            <Form onSubmit={handleSaveFee}>
              <div className="form-grid">
                <Form.Group className="field-stack span-6">
                  <Form.Label>Nilai referensi billing</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={feeInput}
                    onChange={event => setFeeInput(event.target.value)}
                    placeholder="Contoh: 500000"
                  />
                </Form.Group>
                <div className="field-stack span-6 accounting-action-stack">
                  <Form.Label>&nbsp;</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    <Button type="submit" disabled={!selectedOutlet || savingFee}>
                      <FaMoneyCheckAlt /> {savingFee ? 'Saving...' : 'Simpan referensi billing'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      onClick={() => navigate(`/pos-account-mappings?outlet_id=${selectedOutlet}&template=fee:expense`)}
                      disabled={!selectedOutlet}
                    >
                      <FaEdit /> Mapping fee opsional
                    </Button>
                  </div>
                </div>
              </div>
            </Form>

            <div className="accounting-inline-note">
              Mapping fee tidak lagi wajib untuk sync sale POS. Gunakan hanya jika nanti billing outlet bulanan ingin dijurnal terpisah.
            </div>
          </section>

          <section className="section-card accounting-insight-card">
            <div className="accounting-section-head">
              <div>
                <span className="page-eyebrow">Ledger snapshot</span>
                <h2>Current month verification</h2>
                <p>Ringkasan ini diambil dari ledger pusat untuk `source_system = pos`. Detail penuh `trial balance`, `laba rugi`, dan `neraca` sekarang dibaca dari `Analytics Center`.</p>
              </div>
              <span className="status-pill">
                <FaCheckCircle /> Central ledger
              </span>
            </div>

            <div className="accounting-mini-stats">
              <div className="accounting-mini-stat">
                <strong>{formatCurrency(profitLoss?.revenue)}</strong>
                <small>Revenue bulan ini</small>
              </div>
              <div className="accounting-mini-stat">
                <strong>{formatCurrency(profitLoss?.expenses)}</strong>
                <small>Expenses bulan ini</small>
              </div>
              <div className="accounting-mini-stat">
                <strong>{formatCurrency(profitLoss?.net_profit)}</strong>
                <small>Net profit bulan ini</small>
              </div>
              <div className="accounting-mini-stat">
                <strong>{formatCurrency(balanceSheet?.assets)}</strong>
                <small>Total assets</small>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 mt-3">
              <Button
                variant="primary"
                onClick={() => navigate(buildAccountingReportCenterPath('trial-balance', { outletId: selectedOutlet }))}
                disabled={!canOpenAnalyticsCenter}
              >
                <FaChartLine /> Trial Balance
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(buildAccountingReportCenterPath('profit-loss', { outletId: selectedOutlet }))}
                disabled={!canOpenAnalyticsCenter}
              >
                <FaReceipt /> Profit & Loss
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(buildAccountingReportCenterPath('balance-sheet', { outletId: selectedOutlet }))}
                disabled={!canOpenAnalyticsCenter}
              >
                <FaBalanceScale /> Balance Sheet
              </Button>
            </div>
          </section>
        </div>

        <div className="section-card accounting-alert-card">
          <div className="accounting-alert-card__copy">
            <span className="page-eyebrow">Page moved</span>
            <h2>Expenses & Payables sekarang ada di halaman terpisah</h2>
            <p>
              Input beban usaha, hutang, pembayaran hutang, dan report owner dipindahkan ke page khusus supaya
              `Accounting Control` tetap fokus ke akun, mapping, sinkronisasi, dan setup aset.
            </p>
          </div>
          <div className="accounting-alert-card__actions">
            <Button
              variant="primary"
              onClick={() => navigate(buildAccountingReportCenterPath('trial-balance', { outletId: selectedOutlet }))}
              disabled={!canOpenAnalyticsCenter}
            >
              <FaChartLine /> Buka Analytics Center
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => navigate(`/operasional-finance?outlet_id=${selectedOutlet}`)}
              disabled={!selectedOutlet}
            >
              <FaReceipt /> Buka Expenses & Payables
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => navigate(`/asset-management?outlet_id=${selectedOutlet}`)}
              disabled={!selectedOutlet}
            >
              <FaBuilding /> Buka Assets & Investments
            </Button>
          </div>
        </div>

        {showEmbeddedFitnessFinance ? (
        <>
        <div className="accounting-section-break">
          <span className="page-eyebrow">Fitness finance</span>
          <h2>Operasional outlet dan report owner</h2>
          <p>
            Input beban usaha, catat hutang, lunasi hutang, lalu cek ringkasan laba rugi dan neraca outlet fitness di
            bulan {currentPeriod.label}.
          </p>
        </div>

        <div className="accounting-kpi-grid">
          <section className="section-card accounting-kpi-card">
            <span className="page-eyebrow">Fitness revenue</span>
            <h3>{formatCurrency(ownerSummary?.profit_loss?.revenue)}</h3>
            <p>Pendapatan yang sudah masuk ke ledger `fitness` pada periode berjalan.</p>
          </section>
          <section className="section-card accounting-kpi-card">
            <span className="page-eyebrow">Beban usaha</span>
            <h3>{formatCurrency(ownerSummary?.profit_loss?.expenses)}</h3>
            <p>Total beban dari dokumen operasional, hutang usaha, dan posting fitness lain pada bulan ini.</p>
          </section>
          <section className="section-card accounting-kpi-card">
            <span className="page-eyebrow">Net profit</span>
            <h3>{formatCurrency(ownerSummary?.profit_loss?.net_profit)}</h3>
            <p>Laba bersih outlet untuk period owner review saat ini.</p>
          </section>
          <section className="section-card accounting-kpi-card">
            <span className="page-eyebrow">Outstanding hutang</span>
            <h3>{formatCurrency(ownerSummary?.payable_summary?.outstanding_total)}</h3>
            <p>{ownerSummary?.payable_summary?.open_bills || 0} dokumen masih terbuka dan perlu dipantau jatuh temponya.</p>
          </section>
        </div>

        <div className="accounting-panel-grid">
          <section className="section-card accounting-insight-card">
            <div className="accounting-section-head">
              <div>
                <span className="page-eyebrow">Input operasional</span>
                <h2>Beban usaha dan beban lain-lain</h2>
                <p>Pilih bayar langsung atau catat sebagai hutang. Semua dokumen akan posting ke ledger pusat `fitness`.</p>
              </div>
              <span className="status-pill">
                <FaReceipt /> Finance doc
              </span>
            </div>

            <Form onSubmit={handleSaveFinanceDocument}>
              <div className="form-grid">
                <Form.Group className="field-stack span-4">
                  <Form.Label>Metode pencatatan</Form.Label>
                  <Form.Select
                    value={financeForm.settlement_mode}
                    onChange={event => setFinanceForm(prev => ({ ...prev, settlement_mode: event.target.value }))}
                  >
                    {FINANCE_SETTLEMENT_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="field-stack span-4">
                  <Form.Label>Tanggal dokumen</Form.Label>
                  <Form.Control
                    type="date"
                    value={financeForm.expense_date}
                    onChange={event => setFinanceForm(prev => ({ ...prev, expense_date: event.target.value }))}
                    required
                  />
                </Form.Group>
                <Form.Group className="field-stack span-4">
                  <Form.Label>Kategori</Form.Label>
                  <Form.Select
                    value={financeForm.category}
                    onChange={event => setFinanceForm(prev => ({ ...prev, category: event.target.value }))}
                  >
                    {FINANCE_CATEGORY_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="field-stack span-6">
                  <Form.Label>Vendor / payee</Form.Label>
                  <Form.Control
                    value={financeForm.vendor_name}
                    onChange={event => setFinanceForm(prev => ({ ...prev, vendor_name: event.target.value }))}
                    placeholder="Contoh: PLN, owner building, vendor ATK"
                  />
                </Form.Group>
                <Form.Group className="field-stack span-6">
                  <Form.Label>Nomor referensi</Form.Label>
                  <Form.Control
                    value={financeForm.reference_no}
                    onChange={event => setFinanceForm(prev => ({ ...prev, reference_no: event.target.value }))}
                    placeholder="No invoice / kuitansi"
                  />
                </Form.Group>
                <Form.Group className="field-stack span-12">
                  <Form.Label>Deskripsi</Form.Label>
                  <Form.Control
                    value={financeForm.description}
                    onChange={event => setFinanceForm(prev => ({ ...prev, description: event.target.value }))}
                    placeholder="Contoh: Sewa outlet Maret 2026"
                    required
                  />
                </Form.Group>
                <Form.Group className="field-stack span-4">
                  <Form.Label>Nominal</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="1"
                    value={financeForm.amount}
                    onChange={event => setFinanceForm(prev => ({ ...prev, amount: event.target.value }))}
                    placeholder="0"
                    required
                  />
                </Form.Group>
                <Form.Group className="field-stack span-4">
                  <Form.Label>Akun beban</Form.Label>
                  <Form.Select
                    value={financeForm.expense_account_id}
                    onChange={event => setFinanceForm(prev => ({ ...prev, expense_account_id: event.target.value }))}
                    required
                  >
                    <option value="">Pilih akun beban</option>
                    {expenseAccounts.map(account => (
                      <option key={account.account_id} value={account.account_id}>
                        {account.account_id} - {account.account_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                {financeForm.settlement_mode === 'payable' ? (
                  <>
                    <Form.Group className="field-stack span-4">
                      <Form.Label>Jatuh tempo</Form.Label>
                      <Form.Control
                        type="date"
                        value={financeForm.due_date}
                        onChange={event => setFinanceForm(prev => ({ ...prev, due_date: event.target.value }))}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="field-stack span-6">
                      <Form.Label>Akun hutang</Form.Label>
                      <Form.Select
                        value={financeForm.payable_account_id}
                        onChange={event => setFinanceForm(prev => ({ ...prev, payable_account_id: event.target.value }))}
                        required
                      >
                        <option value="">Pilih akun hutang</option>
                        {liabilityAccounts.map(account => (
                          <option key={account.account_id} value={account.account_id}>
                            {account.account_id} - {account.account_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </>
                ) : (
                  <>
                    <Form.Group className="field-stack span-4">
                      <Form.Label>Metode bayar</Form.Label>
                      <Form.Select
                        value={financeForm.payment_method}
                        onChange={event => setFinanceForm(prev => ({ ...prev, payment_method: event.target.value }))}
                      >
                        {FINANCE_SETTLEMENT_OPTIONS.filter(option => option.value !== 'payable').map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="field-stack span-8">
                      <Form.Label>Akun pembayaran</Form.Label>
                      <Form.Select
                        value={financeForm.payment_account_id}
                        onChange={event => setFinanceForm(prev => ({ ...prev, payment_account_id: event.target.value }))}
                        required
                      >
                        <option value="">Pilih akun asset</option>
                        {assetAccounts.map(account => (
                          <option key={account.account_id} value={account.account_id}>
                            {account.account_id} - {account.account_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </>
                )}
                <Form.Group className="field-stack span-12">
                  <Form.Label>Catatan</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={financeForm.notes}
                    onChange={event => setFinanceForm(prev => ({ ...prev, notes: event.target.value }))}
                    placeholder="Catatan tambahan untuk owner atau audit."
                  />
                </Form.Group>
                <div className="field-stack span-12 accounting-action-stack">
                  <div className="d-flex flex-wrap gap-2">
                    <Button type="submit" disabled={!selectedOutlet || savingFinanceDocument || expenseAccounts.length === 0}>
                      <FaPlus /> {savingFinanceDocument ? 'Menyimpan...' : 'Simpan dokumen finance'}
                    </Button>
                    <Button type="button" variant="outline-secondary" onClick={resetFinanceForm}>
                      Reset form
                    </Button>
                  </div>
                </div>
              </div>
            </Form>

            <div className="accounting-inline-note">
              Flow yang dipakai owner: `buat dokumen`  jika hutang maka masuk outstanding - saat dibayar akan membuat jurnal
              `debit hutang / credit kas-bank`.
            </div>
          </section>

          <section className="section-card accounting-insight-card">
            <div className="accounting-section-head">
              <div>
                <span className="page-eyebrow">Pembayaran hutang</span>
                <h2>Lunasi vendor bill</h2>
                <p>Pilih dokumen yang masih outstanding. Saldo hutang dan history payment akan diperbarui otomatis.</p>
              </div>
              <span className={`status-pill ${outstandingPayableDocuments.length ? 'is-warning' : ''}`}>
                <FaFileInvoiceDollar /> {outstandingPayableDocuments.length} open bill
              </span>
            </div>

            <Form onSubmit={handleSavePayablePayment}>
              <div className="form-grid">
                <Form.Group className="field-stack span-12">
                  <Form.Label>Dokumen hutang</Form.Label>
                  <Form.Select
                    value={payablePaymentForm.document_id}
                    onChange={event => setPayablePaymentForm(prev => ({ ...prev, document_id: event.target.value, amount: '' }))}
                    required
                  >
                    <option value="">Pilih hutang yang akan dibayar</option>
                    {outstandingPayableDocuments.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.document_no} - {item.description} - sisa {formatCurrency(item.outstanding_amount)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="field-stack span-4">
                  <Form.Label>Tanggal payment</Form.Label>
                  <Form.Control
                    type="date"
                    value={payablePaymentForm.payment_date}
                    onChange={event => setPayablePaymentForm(prev => ({ ...prev, payment_date: event.target.value }))}
                    required
                  />
                </Form.Group>
                <Form.Group className="field-stack span-4">
                  <Form.Label>Nominal payment</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={payablePaymentForm.amount}
                    onChange={event => setPayablePaymentForm(prev => ({ ...prev, amount: event.target.value }))}
                    placeholder="0"
                    required
                  />
                </Form.Group>
                <Form.Group className="field-stack span-4">
                  <Form.Label>Metode bayar</Form.Label>
                  <Form.Select
                    value={payablePaymentForm.payment_method}
                    onChange={event => setPayablePaymentForm(prev => ({ ...prev, payment_method: event.target.value }))}
                  >
                    {FINANCE_SETTLEMENT_OPTIONS.filter(option => option.value !== 'payable').map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="field-stack span-12">
                  <Form.Label>Akun pembayaran</Form.Label>
                  <Form.Select
                    value={payablePaymentForm.payment_account_id}
                    onChange={event => setPayablePaymentForm(prev => ({ ...prev, payment_account_id: event.target.value }))}
                    required
                  >
                    <option value="">Pilih akun asset</option>
                    {assetAccounts.map(account => (
                      <option key={account.account_id} value={account.account_id}>
                        {account.account_id} - {account.account_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="field-stack span-12">
                  <Form.Label>Catatan payment</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={payablePaymentForm.notes}
                    onChange={event => setPayablePaymentForm(prev => ({ ...prev, notes: event.target.value }))}
                    placeholder="Catatan pembayaran hutang"
                  />
                </Form.Group>
                <div className="field-stack span-12 accounting-action-stack">
                  <div className="d-flex flex-wrap gap-2">
                    <Button type="submit" disabled={!selectedOutlet || savingFinancePayment || outstandingPayableDocuments.length === 0}>
                      <FaMoneyCheckAlt /> {savingFinancePayment ? 'Menyimpan...' : 'Simpan pembayaran hutang'}
                    </Button>
                    <Button type="button" variant="outline-secondary" onClick={resetPayablePaymentForm}>
                      Reset form
                    </Button>
                  </div>
                </div>
              </div>
            </Form>

            <div className="accounting-inline-note">
              {selectedPayableDocument ? (
                <>
                  Dokumen terpilih: <strong>{selectedPayableDocument.document_no}</strong> dengan outstanding{' '}
                  <strong>{formatCurrency(selectedPayableDocument.outstanding_amount)}</strong>.
                </>
              ) : (
                'Pilih dokumen hutang yang masih outstanding untuk men-generate jurnal pembayaran.'
              )}
            </div>
          </section>
        </div>

        <div className="accounting-panel-grid">
          <section className="section-card accounting-insight-card">
            <div className="accounting-section-head">
              <div>
                <span className="page-eyebrow">Owner snapshot</span>
                <h2>Laporan akuntansi bulan ini</h2>
                <p>Ringkasan ini diambil dari ledger `fitness`, jadi pendapatan fitness dan beban operasional bertemu di view yang sama.</p>
              </div>
              <span className="status-pill">
                <FaChartLine /> {currentPeriod.label}
              </span>
            </div>

            <div className="accounting-mini-stats">
              <div className="accounting-mini-stat">
                <strong>{formatCurrency(ownerSummary?.profit_loss?.revenue)}</strong>
                <small>Revenue fitness</small>
              </div>
              <div className="accounting-mini-stat">
                <strong>{formatCurrency(ownerSummary?.profit_loss?.expenses)}</strong>
                <small>Beban usaha</small>
              </div>
              <div className="accounting-mini-stat">
                <strong>{formatCurrency(ownerSummary?.balance_sheet?.assets)}</strong>
                <small>Total assets</small>
              </div>
              <div className="accounting-mini-stat">
                <strong>{formatCurrency(ownerSummary?.balance_sheet?.liabilities)}</strong>
                <small>Total liabilities</small>
              </div>
              <div className="accounting-mini-stat">
                <strong>{formatCurrency(ownerSummary?.balance_sheet?.equity)}</strong>
                <small>Equity</small>
              </div>
              <div className="accounting-mini-stat">
                <strong>{formatCurrency(ownerSummary?.payable_summary?.overdue_total)}</strong>
                <small>Hutang lewat jatuh tempo</small>
              </div>
            </div>

            <div className="accounting-inline-note">
              Page ini tetap menampilkan snapshot owner pack, tapi report accounting utama seperti `trial balance`, `laba rugi`, dan
              `neraca` sekarang sudah tersedia di `Analytics Center`.
            </div>
          </section>

          <section className="section-card accounting-insight-card">
            <div className="accounting-section-head">
              <div>
                <span className="page-eyebrow">Report flow</span>
                <h2>Alur review yang disarankan</h2>
                <p>Supaya owner tidak lompat-lompat angka, review dibagi dari harian sampai audit trail.</p>
              </div>
              <span className="status-pill">
                <FaBalanceScale /> Owner flow
              </span>
            </div>

            <div className="accounting-report-flow">
              {OWNER_REPORT_FLOW.map(item => (
                <div key={item.title} className="accounting-report-flow__item">
                  <strong>{item.title}</strong>
                  <small>{item.copy}</small>
                </div>
              ))}
            </div>

            <div className="accounting-breakdown-list">
              {(ownerSummary?.expense_breakdown || []).slice(0, 6).map(item => (
                <div key={item.category} className="accounting-breakdown-item">
                  <span>{humanizeToken(item.category)}</span>
                  <strong>{formatCurrency(item.total)}</strong>
                </div>
              ))}
              {!(ownerSummary?.expense_breakdown || []).length ? (
                <div className="accounting-inline-note mt-0">Belum ada breakdown beban pada periode ini.</div>
              ) : null}
            </div>
          </section>
        </div>

        <section className="section-card table-card">
          <div className="table-toolbar">
            <span className="status-chip">
              <FaReceipt /> {financeDocuments.length} dokumen finance terbaru
            </span>
            <span className="status-chip">
              <FaFileInvoiceDollar /> {ownerSummary?.payable_summary?.open_bills || 0} hutang masih terbuka
            </span>
          </div>
          <AdminDataTable
            columns={financeDocumentColumns}
            data={financeDocuments}
            progressPending={financeLoading}
            pagination
            highlightOnHover
            striped
            dense
            emptyTitle="Belum ada dokumen finance"
            emptyDescription="Beban usaha, hutang, dan top up pembayaran vendor akan muncul di sini."
          />
        </section>

        <div className="accounting-panel-grid">
          <section className="section-card table-card">
            <div className="table-toolbar">
              <span className="status-chip">
                <FaMoneyCheckAlt /> {financePayments.length} payment hutang terbaru
              </span>
            </div>
            <AdminDataTable
              columns={financePaymentColumns}
              data={financePayments}
              progressPending={financeLoading}
              pagination
              highlightOnHover
              striped
              dense
              emptyTitle="Belum ada pembayaran hutang"
              emptyDescription="Payment terhadap hutang vendor akan tercatat di sini."
            />
          </section>

          <section className="section-card table-card">
            <div className="table-toolbar">
              <span className="status-chip">
                <FaCheckCircle /> {(ownerSummary?.recent_journals || []).length} jurnal terbaru
              </span>
            </div>
            <AdminDataTable
              columns={recentJournalColumns}
              data={ownerSummary?.recent_journals || []}
              progressPending={financeLoading}
              pagination
              highlightOnHover
              striped
              dense
              emptyTitle="Belum ada jurnal fitness"
              emptyDescription="Aktivitas jurnal operasional fitness terbaru akan tampil di sini."
            />
          </section>
        </div>
        </>
        ) : null}

        <section className="section-card table-card">
          <div className="table-toolbar">
            <span className="status-chip">
              <FaWallet /> {centralAccounts.length} akun pusat
            </span>
            <span className="status-chip">
              <FaBalanceScale /> COA ini dipakai bersama oleh modul operasional dan `POS`
            </span>
          </div>
          {!canManageCentralAccounts ? (
            <div className="accounting-inline-note mb-3">
              Akun pusat hanya bisa ditambah, diubah, atau dihapus oleh superadmin. Admin lain tetap bisa melihat daftar akun untuk keperluan mapping.
            </div>
          ) : null}
          <AdminDataTable
            columns={centralAccountColumns}
            data={centralAccounts}
            progressPending={loading}
            pagination
            highlightOnHover
            striped
            dense
            emptyTitle="No central accounts"
            emptyDescription="Tambahkan chart of accounts pusat agar mapping POS bisa memakai kode akun yang valid."
          />
        </section>

        <section className="section-card table-card">
          <div className="table-toolbar">
            <span className="status-chip">
              <FaCheckCircle /> {checklist.length} checklist items
            </span>
            <span className="status-chip">
              <FaExclamationTriangle /> Extended items seperti `qris`, `transfer`, dan `credit` hanya wajib jika outlet memakainya
            </span>
          </div>
          <AdminDataTable
            columns={columns}
            data={checklist}
            progressPending={loading}
            pagination
            highlightOnHover
            striped
            dense
            emptyTitle="No accounting setup data"
            emptyDescription="Pilih outlet untuk memuat checklist setup akuntansi."
          />
        </section>
      </Container>

      <Modal show={showCentralAccountModal} onHide={closeCentralAccountModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{centralAccountForm.id ? 'Edit akun pusat' : 'Tambah akun pusat'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveCentralAccount}>
          <Modal.Body>
            <div className="form-grid">
              <Form.Group className="field-stack span-6">
                <Form.Label>Account ID</Form.Label>
                <Form.Control
                  value={centralAccountForm.account_id}
                  onChange={event => setCentralAccountForm(prev => ({ ...prev, account_id: event.target.value }))}
                  placeholder="Contoh: 402"
                  required
                />
              </Form.Group>
              <Form.Group className="field-stack span-6">
                <Form.Label>Account type</Form.Label>
                <Form.Select
                  value={centralAccountForm.account_type}
                  onChange={event => setCentralAccountForm(prev => ({ ...prev, account_type: event.target.value }))}
                >
                  {ACCOUNT_TYPE_OPTIONS.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="field-stack span-12">
                <Form.Label>Account name</Form.Label>
                <Form.Control
                  value={centralAccountForm.account_name}
                  onChange={event => setCentralAccountForm(prev => ({ ...prev, account_name: event.target.value }))}
                  placeholder="Contoh: POS Revenue"
                  required
                />
              </Form.Group>
              <Form.Group className="field-stack span-12">
                <Form.Label>Opening balance</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={centralAccountForm.balance}
                  onChange={event => setCentralAccountForm(prev => ({ ...prev, balance: event.target.value }))}
                />
              </Form.Group>
            </div>
            <div className="accounting-inline-note mt-3">
              Account ID di sini harus sama persis dengan kode yang akan dipakai oleh mapping akun POS dan posting jurnal pusat.
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeCentralAccountModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={savingCentralAccount}>
              {savingCentralAccount ? 'Saving...' : centralAccountForm.id ? 'Update account' : 'Create account'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default AccountingSetup;
